import { useAuth0 } from '@auth0/auth0-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';

import { useAppLoading } from '@/contexts/LoadingProvider';
import { NotificationCenterProvider } from '@/contexts/NotificationCenterProvider';
import { VNModalProvider } from '@/modals/VNModalProvider';
import { VNMSNavBell } from '@/pages/VNMS/shared/components/VNMSNavBell';

import useConfirm from '../../hooks/useConfirm';
import { applicationDataSelector } from '../../store/slices/application-data';
import { clientSettingsSelector } from '../../store/slices/clientsettings';
import { fuelUserAccessSliceSelector } from '../../store/slices/fuelUserAccess';
import { cacheUtils } from '../../utils/cacheUtils';
import { NO_ACCESSIBLE_OUS_CONFIRM } from '../../utils/userAccessUtils';
import FuelPrompt from '../fuel-prompt/fuel-prompt';
import FuelSnackbar from '../fuel-snackbar';
import { LayoutTitle } from './layout-title/layout-title';
import OuSwitcher from './OuSwitcher';
import { Sidebar } from './sidebar/sidebar';
import ViewNexaSideBar from './sidebar/ViewNexaSideBar';
import { UserNav } from './user-nav/user-nav';

export const Layout = memo(() => {
  const { toastMessage, promptMessage, ouData } = useSelector(applicationDataSelector);
  const { permissions, fuelUserAccessData, accessibleOUs: accessibleOUsFromStore } = useSelector(fuelUserAccessSliceSelector);
  const accessibleOUs = accessibleOUsFromStore ?? fuelUserAccessData?.accessibleOUs;
  const { isLoading, isAuthenticated, logout } = useAuth0();

  const { appLoading, setAppLoading } = useAppLoading();
  const { vnConfirmFn } = useConfirm();
  const navigate = useNavigate();
  const availableOptions = useMemo(
    () =>
      (ouData?.metaData?.ouFeatures?.navigation && {
        ...ouData?.metaData?.ouFeatures?.navigation,
        // always available
        settings: true,
        'settings-legacy-admin': true, // legacy admin
        ouNavigation: true,
        vnms: true, // Notification Admin — fixed on in OU Navigation; sidebar still SuperAdmin-only
      }) ||
      null,
    [ouData?.metaData?.ouFeatures?.navigation]
  );

  const { isToggleSidebarOpen } = useSelector(clientSettingsSelector);
  const flags = useFlags();

  const newUi = useMemo(() => {
    // console.log(flags);
    return flags['enable-view-nexa-new-ui-design'] === true;
  }, [flags]);

  const handleLogout = useCallback(async () => {
    cacheUtils.doHardRefresh();
    logout({ returnTo: window.location.origin });
  }, []);

  useEffect(() => {
    if (fuelUserAccessData && (!accessibleOUs || accessibleOUs.length === 0)) {
      console.warn('[useraccess] No accessible OUs; logging out.');
      setAppLoading(false);
      void (async () => {
        await vnConfirmFn(NO_ACCESSIBLE_OUS_CONFIRM);
        logout({ returnTo: window.location.origin });
      })();
      return;
    }
    if (!permissions) {
      return;
    }
    setAppLoading(false);
    if (accessibleOUs == null || accessibleOUs.length === 0) {
      navigate('oops');
    }
    if (permissions.lookup == null || permissions.lookup.size === 0) {
      navigate('nopermissions');
    }
  }, [accessibleOUs, permissions, fuelUserAccessData, logout, vnConfirmFn]);

  // logged out
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setAppLoading(false);
    }
  }, [isLoading, isAuthenticated]);

  if (appLoading) {
    return null;
  }

  return (
    <div className="layout appInitialized">
      <div className="app-wrapper">
        <VNModalProvider>
          <NotificationCenterProvider>
            {newUi ? (
              <ViewNexaSideBar preview={false} availableOptions={availableOptions} />
            ) : (
              <Sidebar preview={false} availableOptions={availableOptions} />
            )}

            <div className={isToggleSidebarOpen ? 'content-wrapper-expand' : 'content-wrapper'}>
              <div className="layout-content-header" id="fuel-navbar">
                <div className="left">
                  <LayoutTitle />
                </div>

                <div className="right">
                  <div className="header-actions">
                    <OuSwitcher />
                    <VNMSNavBell />
                    <UserNav handleLogout={handleLogout} />
                  </div>
                  {/* <div className="version-label">version: {config.appVersion}</div> */}
                </div>
              </div>
              <div />
              <div className="layout-content-body route-view">
                <FuelSnackbar toastMessage={toastMessage} />
                <FuelPrompt value={promptMessage} />

                <Outlet />
              </div>
            </div>
          </NotificationCenterProvider>
        </VNModalProvider>
      </div>
    </div>
  );
});
